import { debounce, call, put, select, takeEvery, take } from 'redux-saga/effects'
import DashboardSchemaModel, { BlocksOrderedMap } from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'
import BlockModel from '@aytm/app/insights/dashboard/models/BlockModel'
import { TypedSagaDuck } from './PortalDuck.types'
import { PORTAL_ACTIONS } from './PortalDuck.actions'
import { ErrorDuck, ERROR_ACTIONS } from '../ErrorDuck'
import { DataDuck, DATA_ACTIONS } from '../DataDuck'
import { DashboardDuck, DASHBOARD_ACTIONS } from '../DashboardDuck'
import { copyOrCreateBlock, addBlock, replaceView } from '@aytm/app/insights/dashboard/helpers/views'
import { createInitiateLoading } from '../DataDuck/DataDuck.actionCreators'
import {
  updateCurrentData,
  updateCurrentSchema,
  updateCurrentBlock,
  updateRemoteSchema,
  updateState,
  mergeSources,
  saveCurrentSchema,
  getCurrentBlock,
  getCurrentBlockUID,
  updateBlock,
  updateLoading
} from '@aytm/app/insights/dashboard/helpers/requests'
import { deleteBlock, revertBlock } from '@aytm/app/insights/dashboard/helpers/schema'

import { updateSourcesSaga, updateBlock_Saga, updateBlocks_Saga, updateSchemaSaga } from './PortalDuck.sagas'

import { duckName } from './constants'
import { safelyExecuteSaga } from '@aytm/app/insights/dashboard/lib/safety'

const PORTAL_DEBOUNCE_TIMEOUT = 500

const PortalDuck = TypedSagaDuck(duckName, 'insights-dashboard')

// actions
PortalDuck.action(PORTAL_ACTIONS.UPDATE_DATA)
PortalDuck.action(PORTAL_ACTIONS.UPDATE_SCHEMA)
PortalDuck.action(PORTAL_ACTIONS.UPDATE_BLOCK)
PortalDuck.action(PORTAL_ACTIONS.REVERT_SOURCES)
PortalDuck.action(PORTAL_ACTIONS.SAVE_SOURCES)
PortalDuck.action(PORTAL_ACTIONS.SEND_SCHEMA)
PortalDuck.action(PORTAL_ACTIONS.SET_SOURCES)
PortalDuck.action(PORTAL_ACTIONS.RESET_SCHEMA)
PortalDuck.action(PORTAL_ACTIONS.SAVE_CURRENT_VIEW)
PortalDuck.action(PORTAL_ACTIONS.SAVE_CURRENT_VIEW_SET)
PortalDuck.action(PORTAL_ACTIONS.ACTIVATE_VIEW)
PortalDuck.action(PORTAL_ACTIONS.REVERT_CURRENT_VIEW)
PortalDuck.action(PORTAL_ACTIONS.ADD_VIEW)
PortalDuck.action(PORTAL_ACTIONS.ADD_VIEW_SET)
PortalDuck.action(PORTAL_ACTIONS.UPDATE_VIEW_TITLE)
PortalDuck.action(PORTAL_ACTIONS.DELETE_VIEW)
PortalDuck.action(PORTAL_ACTIONS.DELETE_VIEW_SET)
PortalDuck.action(PORTAL_ACTIONS.REPLACE_VIEW)

// selectors

PortalDuck.selector('state', state => state)

PortalDuck.selector('currentData', state => state.currentData)

PortalDuck.selector('currentSchema', state => state.currentSchema)

PortalDuck.selector('remoteSchema', state => state.remoteSchema)

PortalDuck.selector('portalSurveys', state => state.currentData?.portalSurveys)

PortalDuck.selector('currentBlock', state => getCurrentBlock(state))

PortalDuck.selector('currentBlockUID', state => state.currentSchema?.get('currentBlockUID'))

PortalDuck.selector('currentBlocks', state => state.currentSchema?.get('blocks') || BlocksOrderedMap())

// reducers

PortalDuck.reducer(PORTAL_ACTIONS.UPDATE_DATA, (state, action) => updateCurrentData(state, action.payload))

PortalDuck.reducer(PORTAL_ACTIONS.UPDATE_SCHEMA, (state, action) => updateCurrentSchema(state, action.payload))

PortalDuck.reducer(PORTAL_ACTIONS.UPDATE_BLOCK, (state, action) => updateCurrentBlock(state, action.payload))

PortalDuck.reducer(PORTAL_ACTIONS.REVERT_SOURCES, state => {
  let resultState = state
  const remoteSchema = resultState.remoteSchema
  if (remoteSchema) {
    const filters = remoteSchema.get('filters').clone()
    const updatedAt = remoteSchema.get('updatedAt')
    const updatedBy = remoteSchema.get('updatedBy')
    resultState = updateCurrentSchema(resultState, {
      filters,
      updatedAt,
      updatedBy
    })
    resultState = updateCurrentData(resultState, {
      portalFiltersChanged: false,
      portalCurrentFilters: filters.clone()
    })
    resultState.portalModified = false
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.SAVE_SOURCES, state => {
  return {
    ...state,
    isUpdating: true
  }
})

PortalDuck.reducer(PORTAL_ACTIONS.SET_SOURCES, (state, action) => {
  let resultState = state
  const { data } = action.payload || {}
  if (data) {
    resultState = {
      ...state,
      currentSchema: mergeSources(state.currentSchema, data),
      remoteSchema: mergeSources(state.remoteSchema, data),
      portalModified: false,
      isUpdating: false
    }
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.RESET_SCHEMA, (state, action) => {
  let resultState = state
  const { data } = action.payload || {}
  if (data) {
    resultState = {
      ...resultState,
      currentSchema: DashboardSchemaModel.of(data),
      remoteSchema: DashboardSchemaModel.of(data)
    }
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.SAVE_CURRENT_VIEW, (state, action) => {
  let resultState = state
  const { patch } = action.payload || {}
  if (patch) {
    resultState = updateCurrentBlock(resultState, patch)
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.SAVE_CURRENT_VIEW_SET, (state, action) => {
  let resultState = state
  const data = action.payload.data
  const uid = data?.uid
  if (uid) {
    const block = BlockModel.of(data)
    if (block) {
      resultState = {
        ...resultState,
        currentSchema: state.currentSchema.updateIn(['blocks', uid], () => block.clone()),
        remoteSchema: state.remoteSchema.updateIn(['blocks', uid], () => block.clone())
      }
    }
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.ACTIVATE_VIEW, (state, action) => {
  let resultState = updateLoading(state, true)
  const { uid } = action.payload || {}
  if (uid) {
    const block = state.currentSchema.getIn(['blocks', uid])
    if (block) {
      resultState = updateCurrentSchema(resultState, { currentBlockUID: uid })
      const filters = block.get('filters').clone()
      resultState = updateCurrentData(resultState, {
        panelCurrentFilters: filters,
        panelFiltersChanged: false
      })
    }
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.REVERT_CURRENT_VIEW, state => {
  const resultState = updateLoading(state, true)
  return revertBlock(resultState)
})

PortalDuck.reducer(PORTAL_ACTIONS.ADD_VIEW_SET, (state, action) => {
  let resultState = state
  const { block, currentBlocks, remoteBlocks } = action.payload || {}
  if (block && currentBlocks) {
    const blockUid = block.get('uid')
    resultState = updateCurrentSchema(resultState, { currentBlockUID: blockUid, blocks: currentBlocks })
  }
  if (remoteBlocks) {
    resultState = updateRemoteSchema(resultState, { blocks: remoteBlocks })
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.UPDATE_VIEW_TITLE, (state, action) => {
  let resultState = state
  const { uid, title } = action.payload || {}
  if (title) {
    const blockUID = uid || getCurrentBlockUID(state)
    resultState = updateBlock(state, blockUID, { title })
  }
  return resultState
})

PortalDuck.reducer(PORTAL_ACTIONS.DELETE_VIEW_SET, (state, action) => {
  const { uid, schema } = action.payload || {}
  const newCurrentSchema = deleteBlock(state.currentSchema, uid)
  const newRemoteSchema = DashboardSchemaModel.of(schema)
  return updateState(state, { currentSchema: newCurrentSchema, remoteSchema: newRemoteSchema })
})

PortalDuck.reducer(PORTAL_ACTIONS.REPLACE_VIEW, (state, action) => replaceView(state, action.payload))

// sagas

DataDuck.saga(function* init(duckFace) {
  yield take(duckFace.mapActionType('@@INIT'))
  yield take(duckFace.mapActionType(DATA_ACTIONS.ALLOW_REQUEST_SCHEMA))
  const blocks = yield select(duckFace.select.blocks)
  if (0 === blocks?.size) {
    // create new block if there are no blocks in schema
    yield put(PortalDuck.action[PORTAL_ACTIONS.ADD_VIEW]({ patch: { title: 'New Portal' } }))
    yield take(PortalDuck.action[PORTAL_ACTIONS.ADD_VIEW_SET].actionType)
    // this will trigger createInitiateLoading() in another flow
    return
  }
  yield put(createInitiateLoading())
})

PortalDuck.saga(function* run(duckFace) {
  yield takeEvery(
    [
      duckFace.action[PORTAL_ACTIONS.ADD_VIEW_SET].actionType,
      duckFace.action[PORTAL_ACTIONS.DELETE_VIEW_SET].actionType,
      duckFace.action[PORTAL_ACTIONS.UPDATE_VIEW_TITLE].actionType,
      duckFace.action[PORTAL_ACTIONS.REVERT_SOURCES].actionType,
      duckFace.action[PORTAL_ACTIONS.ACTIVATE_VIEW].actionType,
      duckFace.action[PORTAL_ACTIONS.REVERT_CURRENT_VIEW].actionType
    ],
    function* runEvery(action) {
      yield call(saveCurrentSchema, action)
      yield put(createInitiateLoading())
    }
  )
})

PortalDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[PORTAL_ACTIONS.DELETE_VIEW].actionType], function* runEvery(action) {
    const { uid } = action.payload || {}
    const remoteSchema = yield select(duckFace.select.remoteSchema)
    const portalSurveys = yield select(duckFace.select.portalSurveys)
    const newRemoteSchema = deleteBlock(remoteSchema, uid)
    const [updateError, response] = yield call(safelyExecuteSaga, updateSchemaSaga, newRemoteSchema, portalSurveys)
    const { error, data } = response || {}
    yield updateError || error
      ? put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'view', error: updateError || error }))
      : put(duckFace.action[PORTAL_ACTIONS.DELETE_VIEW_SET]({ uid, schema: data }))
  })
})

PortalDuck.saga(function* run(duckFace) {
  yield takeEvery(
    [
      duckFace.action[PORTAL_ACTIONS.UPDATE_SCHEMA].actionType,
      duckFace.action[PORTAL_ACTIONS.UPDATE_BLOCK].actionType,
      duckFace.action[PORTAL_ACTIONS.UPDATE_DATA].actionType,
      duckFace.action[PORTAL_ACTIONS.SAVE_CURRENT_VIEW_SET].actionType
    ],
    function* runEvery(action) {
      yield call(saveCurrentSchema, action)
    }
  )
})

PortalDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[PORTAL_ACTIONS.REPLACE_VIEW].actionType], function* runEvery(action) {
    yield call(saveCurrentSchema, action)

    const currentSchema = yield select(duckFace.select.currentSchema)
    yield put(DashboardDuck.action[DASHBOARD_ACTIONS.SEND_REPORTS_ORDER]({ schema: currentSchema }))
  })
})

PortalDuck.saga(function* run(duckFace) {
  yield debounce(
    PORTAL_DEBOUNCE_TIMEOUT,
    [duckFace.action[PORTAL_ACTIONS.SAVE_SOURCES].actionType],
    function* runOnce() {
      const currentSchema = yield select(duckFace.select.currentSchema)
      const portalSurveys = yield select(duckFace.select.portalSurveys)
      if (currentSchema) {
        yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['sources'] }))
        const [updateEditorError, response] = yield call(
          safelyExecuteSaga,
          updateSourcesSaga,
          currentSchema,
          portalSurveys
        )
        if (updateEditorError) {
          yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'sources', error: updateEditorError.message }))
          return
        }
        const { error, data } = response || {}
        yield error
          ? put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'sources', error }))
          : put(duckFace.action[PORTAL_ACTIONS.SET_SOURCES]({ data }))
      }
    }
  )
})

PortalDuck.saga(function* run(duckFace) {
  yield debounce(
    PORTAL_DEBOUNCE_TIMEOUT,
    [duckFace.action[PORTAL_ACTIONS.SAVE_CURRENT_VIEW].actionType],
    function* runOnce() {
      const currentBlock = yield select(duckFace.select.currentBlock)
      if (currentBlock) {
        const currentBlockUID = yield select(duckFace.select.currentBlockUID)
        yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['view'] }))
        const [updateError, response] = yield call(safelyExecuteSaga, updateBlock_Saga, currentBlock, currentBlockUID)
        const { error, data } = response || {}
        yield updateError || error
          ? put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'view', error: updateError || error }))
          : put(duckFace.action[PORTAL_ACTIONS.SAVE_CURRENT_VIEW_SET]({ data }))
      }
    }
  )
})

PortalDuck.saga(function* run(duckFace) {
  yield debounce(
    PORTAL_DEBOUNCE_TIMEOUT,
    [duckFace.action[PORTAL_ACTIONS.ADD_VIEW].actionType],
    function* runOnce(action) {
      const { uid, patch, position } = action.payload || {}
      const state = yield select(duckFace.select.state)
      const fromUid = uid || state.currentSchema?.get('currentBlockUID')
      const newBlock = copyOrCreateBlock(state, { uid: fromUid, patch })
      if (newBlock) {
        const [currentBlocks, remoteBlocks] = addBlock(state, { block: newBlock, position, revertUid: fromUid })
        yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['view'] }))
        const [error, response] = yield call(safelyExecuteSaga, updateBlocks_Saga, remoteBlocks)
        yield error || response.error
          ? put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'view', error: error || response.error }))
          : put(duckFace.action[PORTAL_ACTIONS.ADD_VIEW_SET]({ block: newBlock, currentBlocks, remoteBlocks }))
      }
    }
  )
})

// module export

export default PortalDuck
